import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Edit,
  FileImage,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/utils/trpc';

interface ExtractedTransaction {
  amount: string;
  type: 'debit' | 'credit';
  date: Date;
  description: string;
  categoryId: string | null;
  notes: string | null;
  householdId: string;
}

interface OCRUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function OCRUploadModal({
  open,
  onOpenChange,
  onSuccess,
}: OCRUploadModalProps) {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [extractedTransactions, setExtractedTransactions] = useState<
    ExtractedTransaction[]
  >([]);
  const [processingStep, setProcessingStep] = useState<
    'upload' | 'processing' | 'review' | 'creating'
  >('upload');

  const processImageMutation = useMutation(
    trpc.ocr.processImage.mutationOptions()
  );
  const createTransactionsMutation = useMutation(
    trpc.ocr.createTransactions.mutationOptions()
  );

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
    },
    multiple: false,
    maxSize: 20 * 1024 * 1024, // 20MB
  });

  const handleProcessImage = async () => {
    if (!uploadedFile) return;

    setProcessingStep('processing');

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        const imageData = base64.split(',')[1]; // Remove data:image/... prefix

        const result = await processImageMutation.mutateAsync({
          imageData,
          mimeType: uploadedFile.type,
          createTransactions: false,
        });

        if (result.success && result.extractionResult) {
          setExtractedTransactions(result.extractionResult.validTransactions);
          setProcessingStep('review');

          if (result.extractionResult.errors.length > 0) {
            toast.warning('Algumas transações não puderam ser processadas', {
              description: result.extractionResult.errors.join(', '),
            });
          }
        } else {
          toast.error('Não foi possível extrair transações da imagem');
          setProcessingStep('upload');
        }
      };

      reader.readAsDataURL(uploadedFile);
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Erro ao processar imagem');
      setProcessingStep('upload');
    }
  };

  const handleCreateTransactions = async () => {
    if (extractedTransactions.length === 0) return;

    setProcessingStep('creating');

    try {
      const result = await createTransactionsMutation.mutateAsync({
        transactions: extractedTransactions,
      });

      if (result.success) {
        toast.success(
          `${result.transactionsCreated} transações criadas com sucesso!`
        );
        onSuccess?.();
        handleClose();
      }
    } catch (error) {
      console.error('Error creating transactions:', error);
      toast.error('Erro ao criar transações');
      setProcessingStep('review');
    }
  };

  const handleClose = () => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setExtractedTransactions([]);
    setProcessingStep('upload');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    onOpenChange(false);
  };

  const removeTransaction = (index: number) => {
    setExtractedTransactions((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog onOpenChange={handleClose} open={open}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Extrair Transações do Extrato</DialogTitle>
          <DialogDescription>
            Envie uma foto do seu extrato bancário ou comprovante para extrair
            as transações automaticamente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {processingStep === 'upload' && (
            <div className="space-y-4">
              {uploadedFile ? (
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileImage className="h-5 w-5" />
                        Imagem Selecionada
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {previewUrl && (
                        <div className="flex justify-center">
                          <img
                            alt="Preview"
                            className="max-h-64 max-w-full rounded-lg border object-contain"
                            src={previewUrl}
                          />
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{uploadedFile.name}</p>
                          <p className="text-muted-foreground text-sm">
                            {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <Button
                          onClick={() => {
                            setUploadedFile(null);
                            setPreviewUrl(null);
                          }}
                          size="sm"
                          variant="outline"
                        >
                          <X className="mr-2 h-4 w-4" />
                          Remover
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex gap-2">
                    <Button className="flex-1" onClick={handleProcessImage}>
                      <Upload className="mr-2 h-4 w-4" />
                      Processar Imagem
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                    isDragActive
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/25 hover:border-primary/50'
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="mb-2 font-medium text-lg">
                    {isDragActive
                      ? 'Solte a imagem aqui...'
                      : 'Arraste e solte sua imagem aqui'}
                  </p>
                  <p className="mb-4 text-muted-foreground text-sm">
                    ou clique para selecionar um arquivo
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Suportado: PNG, JPG, JPEG, WebP (máx. 20MB)
                  </p>
                </div>
              )}
            </div>
          )}

          {processingStep === 'processing' && (
            <div className="flex flex-col items-center justify-center space-y-4 py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="font-medium text-lg">Analisando seu extrato...</p>
              <p className="text-muted-foreground text-sm">
                Isso pode levar alguns segundos
              </p>
            </div>
          )}

          {processingStep === 'review' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">
                  Transações Encontradas ({extractedTransactions.length})
                </h3>
                <Badge variant="secondary">
                  <CheckCircle2 className="mr-1 h-4 w-4" />
                  Processado
                </Badge>
              </div>

              {extractedTransactions.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <AlertCircle className="mb-2 h-8 w-8 text-muted-foreground" />
                    <p className="font-medium text-lg">
                      Nenhuma transação encontrada
                    </p>
                    <p className="text-muted-foreground text-sm">
                      Tente com uma imagem mais clara do seu extrato
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {extractedTransactions.map((transaction, index) => (
                    <Card key={index}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">
                                {transaction.description}
                              </p>
                              <Badge
                                variant={
                                  transaction.type === 'debit'
                                    ? 'destructive'
                                    : 'default'
                                }
                              >
                                {transaction.type === 'debit'
                                  ? 'Débito'
                                  : 'Crédito'}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-muted-foreground text-sm">
                              <span>R$ {transaction.amount}</span>
                              <span>
                                {new Date(
                                  transaction.date
                                ).toLocaleDateString()}
                              </span>
                              {transaction.notes && (
                                <span>{transaction.notes}</span>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => removeTransaction(index)}
                            size="sm"
                            variant="ghost"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {extractedTransactions.length > 0 && (
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={() => setProcessingStep('upload')}
                    variant="outline"
                  >
                    Processar Novamente
                  </Button>
                  <Button className="flex-1" onClick={handleCreateTransactions}>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    Criar {extractedTransactions.length} Transações
                  </Button>
                </div>
              )}
            </div>
          )}

          {processingStep === 'creating' && (
            <div className="flex flex-col items-center justify-center space-y-4 py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="font-medium text-lg">Criando transações...</p>
              <p className="text-muted-foreground text-sm">
                Salvando {extractedTransactions.length} transações no sistema
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
